import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FaCode, FaJava, FaJs, FaPython, FaReact, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import SEO from "../components/SEO";
import matter from "gray-matter";

const blogFiles = [
  "java-tips.md",
  "string-comparision.md",
  "hashset-duplicate.md",
  "netlify-deploy.md",
  "react-joyride.md",
];

const getCategoryIcon = (category) => {
  switch (category) {
    case "java": return <FaJava className="text-orange-600 text-xl mr-2" />;
    case "javascript": return <FaJs className="text-yellow-500 text-xl mr-2" />;
    case "python": return <FaPython className="text-green-600 text-xl mr-2" />;
    case "react": return <FaReact className="text-cyan-500 text-xl mr-2" />;
    default: return <FaCode className="text-gray-500 text-xl mr-2" />;
  }
};

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 3;
  const tabContainerRef = useRef(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      const results = await Promise.all(
        blogFiles.map(async (file) => {
          const res = await import(`../blogs/${file}`);
          const text = await fetch(res.default).then((r) => r.text());
          const { data, content } = matter(text);

          const lines = text.split("\n").filter((line) => line.trim() !== "");
          const firstLine = lines[0]?.trim();

          let firstImage = null;
          const pngMatch = firstLine?.match(/!\[.*?\]\((.*?\.png)\)/i);
          if (pngMatch) firstImage = pngMatch[1];

          let descriptionContent = content.replace(/!\[.*?\]\(.*?\)/g, "").trim();
          if (firstImage) {
            descriptionContent = descriptionContent.split("\n").slice(1).join(" ");
          }

          const description = descriptionContent.substring(0, 150);

          return {
            slug: file.replace(".md", ""),
            title: data.title || file.replace(".md", ""),
            description,
            firstImage,
            content,
            tag: data.main || data.tags?.[0] || "others",
            author: data.author || "CodeRocker",
            date: data.date || new Date().toLocaleDateString(),
          };
        })
      );
      setBlogs(results);
    };
    fetchBlogs();
  }, []);

  const categories = ["all", "java", "react", "javascript", "python", "others"];

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = selectedTab === "all" || blog.tag === selectedTab;
    return matchesSearch && matchesTab;
  });

  const totalPages = Math.ceil(filteredBlogs.length / pageSize);
  const paginatedBlogs = filteredBlogs.slice((page - 1) * pageSize, page * pageSize);

  const scrollTabs = (direction) => {
    if (tabContainerRef.current) {
      tabContainerRef.current.scrollBy({
        left: direction === "right" ? 120 : -120,
        behavior: "smooth",
      });
    }
  };

  // Subtext for SEO and UX
  const categorySubtext = {
    all: "Explore programming tutorials, coding tips, and guides across Java, React, JavaScript, Python, and more.",
    java: "Master Java with tutorials, coding tips, interview questions, and best practices for backend development.",
    react: "Learn React with step-by-step guides, component patterns, hooks usage, and modern frontend techniques.",
    javascript: "Sharpen your JavaScript skills with tutorials, ES6+ concepts, coding tricks, and real-world projects.",
    python: "Discover Python tips, coding practices, data handling tricks, and scripts for automation and AI.",
    others: "Explore other tech tutorials, deployment guides, and development best practices.",
  };

  return (
    <main className="flex flex-col items-center w-full">
      <SEO
        title="Tech Blogs"
        description="Explore tech blogs on Java, React, JavaScript, Python, and more."
        keywords="tech blogs, coding tutorials, programming, java, javascript, react, python, interview preparation"
        url="https://coderocker.netlify.app/blogs"
      />

      <h1 className="text-2xl sm:text-3xl font-semibold mb-4 text-center">
        Tech Blogs
      </h1>

      {/* Dynamic Subtext based on category */}
      <p className="text-gray-600 dark:text-gray-300 mb-6 text-center max-w-2xl">
        {categorySubtext[selectedTab]}
      </p>

      {/* Search */}
      <div className="relative w-full max-w-4xl mb-6">
        <input
          type="text"
          placeholder="Search blogs..."
          className="pl-4 pr-4 py-2 border rounded-full w-full dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search blogs"
        />
      </div>

      {/* Tabs */}
      <nav
        className="flex items-center w-full max-w-4xl mb-4"
        aria-label="Blog categories"
      >
        <button
          className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-2 sm:hidden"
          onClick={() => scrollTabs("left")}
        >
          <FaChevronLeft />
        </button>
        <div
          className="flex gap-2 overflow-x-auto scrollbar-hide flex-1"
          ref={tabContainerRef}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setSelectedTab(cat);
                setPage(1);
              }}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedTab === cat
                  ? "bg-indigo-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700 dark:text-white"
              }`}
              aria-pressed={selectedTab === cat}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        <button
          className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 ml-2 sm:hidden"
          onClick={() => scrollTabs("right")}
        >
          <FaChevronRight />
        </button>
      </nav>

      {/* Blog list */}
      <section className="space-y-4 w-full max-w-4xl">
        {paginatedBlogs.length > 0 ? (
          paginatedBlogs.map((blog) => (
            <article
              key={blog.slug}
              className="border rounded-lg p-6 hover:shadow-lg transition bg-white dark:bg-gray-800"
            >
              {blog.firstImage && (
                <img
                  src={blog.firstImage}
                  alt={`${blog.title} preview`}
                  className="w-full h-48 object-cover rounded mb-4"
                />
              )}
              <header className="flex items-center mb-2">
                {getCategoryIcon(blog.tag)}
                <h2 className="text-lg font-semibold text-indigo-700">
                  <Link to={`/blogs/${blog.slug}`} className="hover:underline">
                    {blog.title}
                  </Link>
                </h2>
              </header>
              <p className="text-gray-700 dark:text-gray-300 line-clamp-3 mb-4">
                {blog.description}
              </p>
              <footer>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Author: {blog.author} • Published on: {blog.date}
                </p>
              </footer>
            </article>
          ))
        ) : (
          <p className="text-center py-10 text-gray-500 text-lg">
            No blogs found.
          </p>
        )}
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav
          className="flex justify-center flex-wrap gap-2 mt-6"
          aria-label="Blog pagination"
        >
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`px-4 py-2 border rounded ${
                page === i + 1
                  ? "bg-indigo-500 text-white"
                  : "bg-white dark:bg-gray-800"
              }`}
              aria-current={page === i + 1 ? "page" : undefined}
            >
              {i + 1}
            </button>
          ))}
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </nav>
      )}
    </main>
  );
};

export default BlogList;
